import type { ReactNode } from 'react';
import * as React from 'react';

import { CardCvcElement, CardExpiryElement, CardNumberElement } from '@stripe/react-stripe-js';
import i18next from 'i18next';

import { MuiBaseTextFiled, StripeInput } from '@shared/ui-kit';

import { StripeTextFieldContainer } from './StripeTextField.styled';

import type { TextFieldProps } from '@mui/material/TextField';
import type {
  AuBankAccountElement,
  FpxBankElement,
  IbanElement,
  IdealBankElement,
} from '@stripe/react-stripe-js';

type StripeElement =
  | typeof AuBankAccountElement
  | typeof CardCvcElement
  | typeof CardExpiryElement
  | typeof CardNumberElement
  | typeof FpxBankElement
  | typeof IbanElement
  | typeof IdealBankElement;

interface StripeTextFieldProps<T extends StripeElement>
  extends Omit<TextFieldProps, 'onChange' | 'inputComponent' | 'inputProps'> {
  inputProps?: React.ComponentProps<T>;
  labelErrorMessage?: string;
  onChange?: React.ComponentProps<T>['onChange'];
  stripeElement?: T;
  isBoldInputLabel?: boolean;
  label?: string;
  inputVariant?: string;
  endIcon?: ReactNode;
}

export const StripeTextField = <T extends StripeElement>(props: StripeTextFieldProps<T>) => {
  const {
    helperText,
    InputLabelProps,
    InputProps = {},
    inputProps,
    error,
    labelErrorMessage,
    stripeElement,
    isBoldInputLabel = false,
    endIcon,
    ...other
  } = props;

  return (
    <StripeTextFieldContainer>
      <MuiBaseTextFiled
        endAdornment={endIcon}
        inputLabelProps={{
          ...InputLabelProps,
        }}
        isBoldInputLabel={isBoldInputLabel}
        isError={error}
        inputProps={{
          ...InputProps,
          inputProps: {
            ...inputProps,
            ...InputProps.inputProps,
            component: stripeElement,
            endAdornment: endIcon,
          },
          inputComponent: StripeInput,
        }}
        errorMessage={labelErrorMessage}
        helperText={error ? '' : helperText}
        {...(other as any)}
      />
    </StripeTextFieldContainer>
  );
};

export const StripeTextFieldNumber = (
  props: StripeTextFieldProps<typeof CardNumberElement> & { labelId?: string; inputId?: string },
) => {
  return (
    <StripeTextField
      label={`${i18next.t('general.fieldNames.cardNumber')}`}
      stripeElement={CardNumberElement}
      {...props}
    />
  );
};

export const StripeTextFieldExpiry = (
  props: StripeTextFieldProps<typeof CardExpiryElement> & { labelId?: string; inputId?: string },
) => {
  return (
    <StripeTextField
      label={`${i18next.t('general.fieldNames.MMYY')}`}
      stripeElement={CardExpiryElement}
      {...props}
    />
  );
};

export const StripeTextFieldCVC = (
  props: StripeTextFieldProps<typeof CardCvcElement> & { labelId?: string; inputId?: string },
) => {
  return (
    <StripeTextField
      label={`${i18next.t('general.fieldNames.security_code')}`}
      stripeElement={CardCvcElement}
      {...props}
    />
  );
};
